<template>
  <Motion.aside
    :initial="false"
    :animate="animateState"
    :variants="sidebarVariants"
    data-testid="sidebar"
    :aria-label="$t('app.sidebar.navigation_label')"
    class="bg-neutral-surface-muted relative after:absolute after:top-0 after:-right-px after:w-px after:h-full after:bg-neutral-border-default after:z-10 sideBar h-full group"
  >
    <div class="w-full h-full flex flex-col overflow-hidden">
      <!-- LOGO AREA -->
      <div class="flex-shrink-0 relative border-b border-neutral-border-default w-full transition-all duration-200 ease-out delay-100"
      :class="sidebar.open ? 'h-[65px]' : 'h-[44px]'"
      >
        <!-- Expanded logo (always in DOM, fades via variant) -->
        <Motion.div
          :variants="expandedLogoVariants"
          data-testid="sidebar-logo"
          class="absolute flex items-start justify-between px-xl pt-[14px] pb-s w-full h-full"
        >
          <router-link
            :to="{ name: 'datasets' }"
            class="flex flex-col gap-2xs"
            :aria-label="$t('app.sidebar.go_to_home')"
          >
            <MeridiaFullLogo class="object-cover w-[100px] h-m" alt="Meridia Logo"/>
            <span class="text-title-small text-[#20C578]" aria-hidden="true">
              Verify
            </span>
          </router-link>
          <button
            v-if="!mobile"
            data-testid="sidebar-toggle"
            type="button"
            :aria-label="$t('app.sidebar.collapse_sidebar')"
            class="flex items-center justify-center cursor-pointer hover:bg-neutral-surface-disabled radius-base p-xs -mt-xs transition-colors"
            @click="sidebar.open = false"
          >
            <MeridiaIcon
              name="view_sidebar"
              type="material-symbols-outlined"
              class="icon-s text-neutral-text-negative-caption"
            />
          </button>
        </Motion.div>

        <!-- Collapsed logo (always in DOM, fades via variant) -->
        <Motion.div
          :variants="collapsedLogoVariants"
          data-testid="sidebar-logo-collapsed"
          class="absolute w-full h-full"
        >
          <button
            type="button"
            :aria-label="$t('app.sidebar.expand_sidebar')"
            class="flex items-center justify-center cursor-pointer w-full h-full"
            @click="sidebar.open = true"
          >
            <div class="relative h-full w-full flex items-center justify-center">
              <MeridiaLogo
                aria-hidden="true"
                class="h-m absolute transition-all duration-200 ease-out group-hover:opacity-0 group-hover:scale-75"
              />
              <MeridiaIcon
                aria-hidden="true"
                name="view_sidebar"
                type="material-symbols-outlined"
                class="icon-s text-neutral-text-caption bg-neutral-surface-disabled radius-base p-xs transition-all duration-200 absolute opacity-0 scale-75 ease-out group-hover:opacity-100 group-hover:scale-100"
              />
            </div>
          </button>
        </Motion.div>
      </div>

      <!-- SCROLLABLE NAVIGATION -->
      <Motion.nav
        :variants="navAreaVariants"
        data-testid="sidebar-navigation"
        :aria-label="$t('app.sidebar.navigation_label')"
        class="flex-1 overflow-y-auto overflow-x-hidden py-s px-m w-full"
      >
        <SidebarNavigationGroup
          :mobile="mobile"
          :navigationItemsGroup="navigationItemsGroup"
        />
      </Motion.nav>

      <!-- USER FOOTER -->
      <div
        data-testid="sidebar-user-footer"
        class="flex-shrink-0 border-t border-neutral-border-default w-full transition-all duration-300 ease-out"
        :class="sidebar.open ? 'h-[80px]' : 'h-[48px] delay-200'"
      >
        <Motion.div
          :variants="footerVariants"
          class="flex items-center"
        >
          <!-- User info: collapses via overflow + opacity -->
          <Motion.div
            :variants="footerTextVariants"
            class="overflow-hidden min-w-0"
            :aria-hidden="!sidebar.open"
          >
            <div class="whitespace-nowrap">
              <div data-testid="sidebar-user-company" class="text-body-small text-neutral-text-caption truncate mb-1">
                {{ getUserCompany }}
              </div>
              <div data-testid="sidebar-user-name" class="text-body-medium text-neutral-text-body truncate">
                {{ getFullUserName }}
              </div>
              <div data-testid="sidebar-user-role" class="text-body-small text-neutral-text-body truncate">
                {{ userStore.getUserRole(userStore.user) }}
              </div>
            </div>
          </Motion.div>

          <!-- Logout button -->
          <div class="flex-shrink-0 flex justify-center">
            <MeridiaButton
              data-testid="sidebar-logout"
              :type="ButtonType.TERTIARY"
              :size="ButtonSize.MINI"
              icon="logout"
              :aria-label="$t('app.sidebar.log_out')"
              @click="() => userStore.signOut()"
            />
          </div>
        </Motion.div>
      </div>
    </div>
  </Motion.aside>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { Motion } from 'src/core/composables/useMotion';
import { useAppStore } from 'src/core/stores/app';
import SidebarNavigationGroup from 'src/core/components/layouts/sidebar/SidebarNavigationGroup.vue';
import { useSidebar } from 'src/core/composables/layouts/sidebar/useSidebar';
import { useUserStore } from 'src/core/stores/user';
import MeridiaIcon from 'src/design_system/components/icon/MeridiaIcon.vue';
import { ButtonType, ButtonSize } from 'src/design_system/components/button/types';
import MeridiaLogo from '@/assets/Meridia_logo.svg?component';
import MeridiaFullLogo from '@/assets/Meridia_full_logo.svg?component';

const { mobile } = defineProps<{
  mobile: boolean;
}>();

const appStore = useAppStore();
const userStore = useUserStore();

const { getFullUserName, getUserCompany } = userStore;
const { navigationItemsGroup } = storeToRefs(appStore);
const { sidebar } = useSidebar();

const animateState = computed(() =>
  mobile ? 'expanded' : sidebar.value.open ? 'expanded' : 'collapsed'
);

// --- Variant definitions ---

const sidebarVariants = {
  expanded: {
    width: 190,
    transition: {
      width: { type: 'spring' as const, stiffness: 400, damping: 30 },
      delayChildren: 0.05,
      staggerChildren: 0.03,
    },
  },
  collapsed: {
    width: 64,
    transition: {
      width: { type: 'spring' as const, stiffness: 400, damping: 30, delay: 0.15 },
      // when: 'afterChildren' as const,
      staggerChildren: 0.02,
    },
  },
};

const expandedLogoVariants = {
  expanded: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25, delay: 0.15 },
  },
  collapsed: {
    opacity: 0,
    x: -10,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
};

const collapsedLogoVariants = {
  expanded: {
    opacity: 0,
    scale: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
  collapsed: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
};

const navAreaVariants = {
  expanded: {
    transition: { type: 'spring' as const, stiffness: 400, damping: 30 },
  },
  collapsed: {
    transition: { type: 'spring' as const, stiffness: 400, damping: 30 },
  },
};

const footerVariants = {
  expanded: {
    paddingLeft: 24,
    paddingRight: 16,
    paddingTop: 16,
    paddingBottom: 16,
    justifyContent: 'space-between',
    transition: { type: 'spring' as const, stiffness: 400, damping: 30 },
  },
  collapsed: {
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 14,
    paddingBottom: 14,
    justifyContent: 'center',
    transition: { type: 'spring' as const, stiffness: 400, damping: 30 },
  },
};

const footerTextVariants = {
  expanded: {
    opacity: 1,
    width: 'auto',
    flexGrow: 1,
    display: 'flex',
    x: 0,
    transition: { duration: 0.2, delay: 0.05 },
  },
  collapsed: {
    opacity: 0,
    width: 0,
    flexGrow: 0,
    x: -10,
    transition: { duration: 0.2 },
    transitionEnd: { display: 'none' },
  },
};

// --- Resize handler ---

// Resolve initial sidebar state synchronously before first render.
// This prevents resizeHandler from changing state post-mount, which would
// trigger a visible spring animation (expanded → collapsed) on page load.
if (!mobile) {
  sidebar.value.open = window.innerWidth >= 1279;
}

onMounted(() => {
  window.addEventListener('resize', resizeHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeHandler);
});

function resizeHandler() {
  if (window.innerWidth < 1279) {
    sidebar.value.open = false;
  } else {
    sidebar.value.open = true;
  }
}
</script>
